import React, { useEffect, useState } from "react";
import { API } from "../utils/api";
import {
    Loader2,
    Wallet,
    AlertCircle,
    ArrowUpRight,
    ArrowDownRight,
    Gift,
    Calendar,
    Check,
    X,
    Search,
    Filter,
    Clock,
    XCircle,
    DollarSign,
} from "lucide-react";

export default function AdminTransactions() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [approveLoading, setApproveLoading] = useState(null);
    const [rejectLoading, setRejectLoading] = useState(null);

    const [walletModal, setWalletModal] = useState(false);
    const [selectedWalletAddress, setSelectedWalletAddress] = useState("");

    const [selectedQr, setSelectedQr] = useState("");


    const [utrModal, setUtrModal] = useState(false);
    const [utrValue, setUtrValue] = useState("");
    const [selectedTxId, setSelectedTxId] = useState(null);






    const loadTransactions = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await API.get("/admin/transactions");
            setTransactions(res.data || []);
        } catch (err) {
            setError("Failed to load transactions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTransactions();
    }, []);

    const handleStatusChange = async (id, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this transaction?`)) return;
        try {
            await API.put(`/wallet/transaction/${id}/status`, { status });
            await loadTransactions();
        } catch (err) {
            alert(err.response?.data?.error || "Action failed");
        }
    };

    const formatDate = (date) =>
        new Date(date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

    const typeMeta = (tx) => {
        if (tx.type === "deposit")
            return {
                label: "Deposit",
                iconBg: "bg-emerald-100",
                iconColor: "text-emerald-600",
                Icon: ArrowUpRight,
                amountColor: "text-emerald-600",
                prefix: "+",
            };
        if (tx.type === "withdraw")
            return {
                label: "Withdraw",
                iconBg: "bg-red-100",
                iconColor: "text-red-600",
                Icon: ArrowDownRight,
                amountColor: "text-red-600",

            };
        return {
            label: "Commission",
            iconBg: "bg-indigo-100",
            iconColor: "text-indigo-600",
            Icon: Gift,
            amountColor: "text-indigo-700",
            prefix: "+",
        };
    };

    const filteredTx = transactions.filter((tx) => {
        const matchesStatus = statusFilter === "all" || tx.status === statusFilter;
        const matchesType = typeFilter === "all" || tx.type === typeFilter;
        const matchesSearch =
            (tx?.userId?.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (tx?.userId?.email || "").toLowerCase().includes(search.toLowerCase());
        return matchesStatus && matchesType && matchesSearch;
    });

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">

            {/* Main Content */}
            <div className="flex-1 p-6 lg:p-12">
                <div className=" mx-auto">

                    {/* Filters - Same style as Clients page */}
                    <div className="bg-white rounded-3xl shadow-xl p-6 mb-8 border border-gray-200">
                        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">

                            {/* 🔍 Search Input (Full width on mobile) */}
                            <div className="relative w-full md:w-96">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by user name or email..."
                                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-300 
                   rounded-2xl focus:outline-none focus:ring-2 
                   focus:ring-gray-600 transition-all text-gray-800 font-medium"
                                />
                            </div>

                            {/* 🔽 Filters Section */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">

                                {/* Status Filter */}
                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="w-full sm:w-auto px-5 py-3 bg-gray-50 border border-gray-300 
                     rounded-xl focus:outline-none focus:ring-2 
                     focus:ring-gray-600 text-gray-800 font-medium"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>

                                {/* Type Filter */}
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="w-full sm:w-auto px-5 py-3 bg-gray-50 border border-gray-300 
                   rounded-xl focus:outline-none focus:ring-2 
                   focus:ring-gray-600 text-gray-800 font-medium"
                                >
                                    <option value="all">All Types</option>
                                    <option value="deposit">Deposit</option>
                                    <option value="withdraw">Withdraw</option>
                                    <option value="commission">Commission</option>
                                </select>
                            </div>

                        </div>
                    </div>


                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3 mb-6 shadow-sm">
                            <AlertCircle className="w-6 h-6 flex-shrink-0" />
                            <span className="font-medium">{error}</span>
                        </div>
                    )}

                    {/* Table */}
                    {!loading ? (
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                        <tr className="text-left text-gray-700">
                                            <th className="py-4 px-6 font-semibold">User</th>
                                            <th className="py-4 px-6 font-semibold">Type</th>
                                            <th className="py-4 px-6 font-semibold">Amount(5% Deduction)</th>
                                            <th className="py-4 px-6 font-semibold">Original Amount</th>
                                            <th className="py-4 px-6 font-semibold">Ref. Code</th>
                                            <th className="py-4 px-6 font-semibold">Wallet Address</th>
                                            <th className="py-4 px-6 font-semibold">Date</th>
                                            {/* <th className="py-4 px-6 font-semibold">Note</th> */}
                                            <th className="py-4 px-6 font-semibold">Status</th>
                                            <th className="py-4 px-6 font-semibold">Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredTx.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-10 text-gray-500">
                                                    No transactions found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredTx.map((tx) => {
                                                const meta = typeMeta(tx);

                                                return (
                                                    <tr
                                                        key={tx._id}
                                                        className={`border-t border-gray-100 transition-all
                                                            ${tx.status === "pending" ? "bg-yellow-50" : ""}
                                                            ${tx.status === "rejected" ? "bg-red-50" : ""}
                                                            hover:bg-gray-50
                                                        `}
                                                    >
                                                        {/* USER */}
                                                        <td className="py-4 px-6">
                                                            <div className="font-semibold text-gray-800">
                                                                {tx?.userId?.name}
                                                            </div>
                                                            <div className="text-gray-500 text-xs">
                                                                {tx?.userId?.email}
                                                            </div>
                                                        </td>

                                                        {/* TYPE */}
                                                        <td className="py-4 px-6">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`p-2 rounded-full ${meta.iconBg}`}>
                                                                    <meta.Icon className={`w-4 h-4 ${meta.iconColor}`} />
                                                                </div>
                                                                <span>{meta.label}</span>
                                                            </div>
                                                        </td>

                                                        {/* AMOUNT */}
                                                        <td className="py-4 px-6 font-semibold">
                                                            {tx.status === "pending" ? (
                                                                <span className="text-yellow-700">
                                                                    <DollarSign className="inline w-4 h-4" />
                                                                    {Number(tx.amount).toLocaleString()}
                                                                </span>
                                                            ) : tx.status === "rejected" ? (
                                                                <span className="text-red-700">
                                                                    <DollarSign className="inline w-4 h-4" />
                                                                    {Number(tx.amount).toLocaleString()}
                                                                </span>
                                                            ) : (
                                                                <span className={meta.amountColor}>
                                                                    {meta.prefix}${Number(tx.amount).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td className="py-4 px-6 font-semibold">
                                                            {tx.type === "withdraw"
                                                                ? `-₹${Number(tx.originalAmount || 0).toLocaleString()}`
                                                                : tx.originalAmount
                                                                    ? `₹${Number(tx.originalAmount).toLocaleString()}`
                                                                    : "---"
                                                            }
                                                        </td>


                                                        <td className="py-4 px-6 text-gray-600 text-sm whitespace-nowrap">
                                                            {tx.referenceCode || '---'}
                                                        </td>

                                                        <td className="py-4 px-6 text-sm">
                                                            {tx.walletAddress ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedWalletAddress(tx.walletAddress);
                                                                        setSelectedQr(tx?.userId?.qrImage || "");
                                                                        setWalletModal(true);
                                                                    }}
                                                                    className="text-blue-600 underline hover:text-blue-800"
                                                                >
                                                                    View Wallet Address
                                                                </button>

                                                            ) : (
                                                                <span className="text-gray-500">---</span>
                                                            )}
                                                        </td>



                                                        {/* DATE */}
                                                        <td className="py-4 px-6 text-gray-600 text-sm whitespace-nowrap">
                                                            <div className="flex items-center gap-1">
                                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                                {formatDate(tx.createdAt || tx.date)}
                                                            </div>
                                                        </td>

                                                        {/* NOTE */}
                                                        {/* <td className="py-4 px-6 text-gray-600 text-sm">
                                                            {tx.note || "—"}
                                                        </td> */}

                                                        {/* STATUS */}
                                                        <td className="py-4 px-6">
                                                            {tx.status === "pending" ? (
                                                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-200 text-yellow-800 flex items-center gap-1">
                                                                    <Clock className="w-3.5 h-3.5" />
                                                                    Pending
                                                                </span>
                                                            ) : tx.status === "rejected" ? (
                                                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-200 text-red-800 flex items-center gap-1">
                                                                    <XCircle className="w-3.5 h-3.5" />
                                                                    Rejected
                                                                </span>
                                                            ) : (
                                                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-200 text-green-700">
                                                                    Approved
                                                                </span>
                                                            )}
                                                        </td>

                                                        {/* ACTION BUTTONS */}
                                                        <td className="py-4 px-6">
                                                            <div className="flex gap-2">

                                                                {/* APPROVE BUTTON */}
                                                                <button
                                                                    onClick={async () => {
                                                                        if (tx.type === "withdraw") {
                                                                            // Open UTR modal ONLY for withdraw
                                                                            setSelectedTxId(tx._id);
                                                                            setUtrValue("");
                                                                            setUtrModal(true);
                                                                        } else {

                                                                            setApproveLoading(tx._id);
                                                                            await handleStatusChange(tx._id, "approved");
                                                                            setApproveLoading(null);
                                                                        }
                                                                    }}


                                                                    disabled={tx.status !== "pending" || approveLoading === tx._id || rejectLoading === tx._id}
                                                                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm transition
                                                                           ${tx.status === "pending"
                                                                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                                                                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        }`}
                                                                >
                                                                    {approveLoading === tx._id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 animate-spin" /> Processing…
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Check className="w-4 h-4" /> Approve
                                                                        </>
                                                                    )}
                                                                </button>

                                                                {/* REJECT BUTTON */}
                                                                <button
                                                                    onClick={async () => {
                                                                        setRejectLoading(tx._id);
                                                                        await handleStatusChange(tx._id, "rejected");
                                                                        setRejectLoading(null);
                                                                    }}
                                                                    disabled={tx.status !== "pending" || approveLoading === tx._id || rejectLoading === tx._id}
                                                                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm transition
                                                                         ${tx.status === "pending"
                                                                            ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        }`}
                                                                >
                                                                    {rejectLoading === tx._id ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 animate-spin" /> Processing…
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <X className="w-4 h-4" /> Reject
                                                                        </>
                                                                    )}
                                                                </button>

                                                            </div>
                                                        </td>


                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center py-20">
                            <Loader2 className="w-12 h-12 animate-spin text-gray-700" />
                        </div>
                    )}
                </div>
            </div>


            {walletModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl relative">

                        <button
                            onClick={() => setWalletModal(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold text-gray-800 mb-4">
                            Wallet Address
                        </h2>

                        <div className="bg-gray-100 p-4 rounded-xl border border-gray-300 break-all text-gray-700">
                            {selectedWalletAddress}
                        </div>

                        {/* QR CODE SHOW */}
                        {selectedQr ? (
                            <div className="mt-4 flex flex-col items-center gap-3">
                                <p className="text-gray-600 font-medium">QR Code</p>
                                <img
                                    src={`https://api.kestren.com${selectedQr}`}
                                    className="w-40 h-40 border rounded-2xl object-cover"
                                    alt="User QR"
                                />
                            </div>
                        ) : (
                            <p className="mt-4 text-gray-500 text-center">No QR Code uploaded</p>
                        )}

                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(selectedWalletAddress);
                            }}
                            className="mt-6 w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700"
                        >
                            Copy Address
                        </button>
                    </div>



                </div>
            )}


            {utrModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white p-6 rounded-2xl max-w-sm w-full shadow-xl relative">

                        <button
                            onClick={() => setUtrModal(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-xl font-bold mb-4 text-gray-800">
                            Enter UTR / Reference Number
                        </h2>

                        <input
                            type="text"
                            value={utrValue}
                            onChange={(e) => setUtrValue(e.target.value)}
                            placeholder="Enter UTR number..."
                            className="w-full px-4 py-3 border rounded-xl mb-4"
                            required
                        />

                        <button
                            onClick={async () => {
                                if (!utrValue.trim()) {
                                    alert("UTR is required");
                                    return;
                                }
                                setApproveLoading(selectedTxId);

                                try {
                                    await API.put(`/wallet/transaction/${selectedTxId}/status`, {
                                        status: "approved",
                                        utr: utrValue.trim()
                                    });

                                    setUtrModal(false);
                                    setApproveLoading(null);
                                    loadTransactions();

                                } catch (err) {
                                    alert(err.response?.data?.error || "Approval failed");
                                    setApproveLoading(null);
                                }
                            }}
                            className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700"
                        >
                          {approveLoading ? "Processing" : " Approve"} 
                        </button>

                    </div>
                </div>
            )}


        </div>
    );
}